import { useMutation, useQuery } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import {
  ApplicationStatus,
  OurPetPolicyAnimal,
  PetScreeningAnimal,
  PetScreeningReportVendors,
  Verification,
  VerificationNames
} from "shared/types";
import { Applicant } from "types/application";
import { BarChart01, LinkExternal01, User03 } from "untitledui-js-base";
import PetsIcon from "../../../../shared/icons/pets.svg?react";
import { getLogoSrc } from "utils/getLogoSrc";
import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { DynamicKeyValue } from "views/applications-id-report/components/SmartRender/DynamicKeyValue";
import { OurPetPolicyReportFields } from "./components/OurPetPolicyReportFields";
import { Link } from "react-router-dom";
import { PetScreeningReportFields } from "./components/PetScreeningReportFields";
import { has } from "shared/utils";
import { useMemo } from "react";
import { Loader } from "shared/components/Loader";
import { EmailSentSection } from "./components/EmailSentSection";
import { ErrorSection } from "./components/ErrorSection";
import { PetScreeningReport, PetVerificationStatusEnum } from "./types";
import api from "api/landlord/applications";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { Stat } from "views/applications-id-report/components/Stat";
import { VerificationReport } from "types/landlord/reports";
import { AxiosError } from "axios";

interface PetScreeningReportSliceProps {
  verification?: Verification;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
}

export const PetScreeningReportSlice = ({
  verification,
  applicant,
  applicationStatus
}: PetScreeningReportSliceProps) => {
  const { id: verificationId } = verification || {};

  const {
    data: verificationData,
    isLoading,
    error,
    refetch
  } = useQuery<VerificationReport<PetScreeningReport>, AxiosError>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId || 0
        )
        .then((data) => data.verification)
  });

  const {
    isPending,
    mutate,
    error: mutationError
  } = useMutation({
    mutationKey: ["PET_SCREENING_REPORT", verificationId],
    mutationFn: () =>
      addressApi.getManualAnimalScreeningReport(verificationId || 0),
    onSuccess: () => {
      refetch();
    }
  });

  const { report, requested_at, updated_at, vendor } = verificationData || {};

  const hasReportData: boolean = useMemo(
    () => Boolean(report && has(report.applicant_info)),
    [report]
  );

  const isPetScreeningVendor =
    vendor && vendor === PetScreeningReportVendors.PetScreening;

  const waitingForReport = useMemo(() => {
    return Boolean(requested_at && !hasReportData);
  }, [requested_at, hasReportData, error, mutationError]);

  const detailsPageUrl = report?.applicant_info?.details_page_url;

  if (isLoading || isPending)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={isPetScreeningVendor ? undefined : mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        <Loader />
      </>
    );

  if (error)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={isPetScreeningVendor ? undefined : mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        {error.code === "400" ? (
          // 400 represents the fact that verification is not YET present on
          // our backend
          <EmailSentSection applicant={applicant} emailSentAt={requested_at} />
        ) : (
          <ErrorSection
            vendor={vendor as PetScreeningReportVendors}
            errorMessage={
              verificationData?.error_details?.body?.message ||
              verificationData?.error ||
              mutationError?.message
            }
          />
        )}
      </>
    );

  if (waitingForReport)
    return (
      <>
        <ReportHeader
          applicationStatus={applicationStatus}
          applicationId={applicant.application_id}
          applicantId={applicant.id}
          name={VerificationNames.PetScreeningReport}
          title="Animal screening"
          onRerunReport={isPetScreeningVendor ? undefined : mutate}
          reRunReportButtonText=""
          reRunLoading={isPending}
          reportDateText="Last updated on"
          reportDate={updated_at}
          hidePrint
        />
        <EmailSentSection applicant={applicant} emailSentAt={requested_at} />
      </>
    );

  return (
    <>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicant.application_id}
        applicantId={applicant.id}
        name={VerificationNames.PetScreeningReport}
        title="Animal screening"
        onRerunReport={isPetScreeningVendor ? undefined : mutate}
        reRunReportButtonText=""
        reRunLoading={isPending}
        reportDateText="Last updated on"
        reportDate={updated_at}
        hidePrint
      />

      <Section
        sectionTitle="Summary"
        maxWidth="100%"
        icon={<BarChart01 size="20px" className="stroke-content-500" />}
      >
        <div className="pt-3 pb-6 border-b border-alpha-40 mb-6">
          <img
            className="h-[22px]"
            src={getLogoSrc(vendor as PetScreeningReportVendors)}
            alt={vendor + "logo"}
          />
        </div>
        <div className="flex justify-between items-center">
          <Stat
            title="Verified Pets or Assistance Animals"
            value={`${
              report?.pets?.filter(
                (pet) =>
                  (pet.verification_status as PetVerificationStatusEnum) ===
                  PetVerificationStatusEnum.VERIFIED
              ).length || 0
            } of ${report?.pets?.length}`}
          />
          <div className="ml-auto">
            <p className="text-content-1000 font-bold text-right text-sm mb-2">
              {isPetScreeningVendor
                ? "PetScreening details page"
                : "View on ourpetpolicy.com"}
            </p>
            <Link
              to={detailsPageUrl || ""}
              className="text-primary-500 text-sm flex gap-2 items-center font-medium underline max-w-[300px] w-full"
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkExternal01
                size="14px"
                strokeWidth={1.5}
                className="shrink-0 stroke-primary-500  "
              />
              <p className="truncate">
                {detailsPageUrl?.replace("https://landlord.", "")}
              </p>
            </Link>
          </div>
        </div>
      </Section>
      {!report?.pets?.length ? (
        <div className="flex flex-col gap-2 justify-center items-center py-20">
          <div className="p-8 rounded-full flex items-center justify-center bg-background-900">
            <PetsIcon className="w-10 h-10 stroke-content-300" />
          </div>
          <h2 className="text-md font-bold text-center text-content-500">
            Applicant stated that they do not have any pets or assistance
            animals
          </h2>
          <p className="text-center text-content-300 text-sm">
            Response from
            <span className="font-bold">
              {isPetScreeningVendor ? " PetScreening" : " OurPetPolicy"}`
            </span>
          </p>
        </div>
      ) : (
        <>
          <Section
            sectionTitle="Applicant info"
            maxWidth="100%"
            icon={<User03 className="stroke-content-400" size="20px" />}
          >
            <DynamicKeyValue
              detailsKey="Name"
              value={`${report.applicant_info?.name}`}
            />
            <DynamicKeyValue
              detailsKey="Email"
              value={report.applicant_info?.email}
            />
            <DynamicKeyValue
              detailsKey="Property manager ID"
              value={
                verificationData?.schema?.settings.settings
                  ?.property_management_id || ""
              }
            />
          </Section>
          <Section
            sectionTitle="Pets / Assistance animals"
            maxWidth="100%"
            icon={<PetsIcon className="size-5 stroke-content-500" />}
          >
            {isPetScreeningVendor ? (
              <PetScreeningReportFields
                animals={report.pets as PetScreeningAnimal[]}
              />
            ) : (
              <OurPetPolicyReportFields
                animals={report.pets as OurPetPolicyAnimal[]}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};
